// @givmo/board — what a record's page keeps of how an imported record came in, and of a file Board
// holds (CBG-AI5-PROPOSE; backend `board-ai-proposals.md` §6.4, §7; CBG-UX11, design-284 §2.3).
//
//   * HOW IT CAME IN, from the record's own read (design-284 §4 B5): a record a connected AI proposed
//     says who it was proposed for, through which client and when, and who imported it and when (copy
//     7.2, approved); a record entered by hand says who entered it and when. The record's read
//     carries those names and dates itself, so no batch is read and none is named (frames r1, r2).
//   * THE FILE (copy 3.6, 5.4): a record with a Board-held file shows its name and size, and opens it
//     through a signed read behind the record's own confidentiality-scoped read: a closed session's
//     file is as closed as its record. The record never carries a storage key or a link (D5).

import { useMutation } from '@tanstack/react-query';
import type { OrgBoardRecordOut, OrgBoardRecordReadOut } from '@givmo/api-client';
import { Icon, toast } from '@givmo/design-system';
import { FileText } from 'lucide-react';
import { useBoardOperations } from '../../board-runtime';
import { displayLongDate } from '../../lib/display';
import { importWords as iw } from '../import/import-words';
import { fileSize } from '../returns/return-decode';
import { servedSentence } from '../returns/return-queries';
import { fileKindWord, isWordFile, proposalWords as w } from './proposal-words';

const LINE = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';

/** A name as the directory gives it, or null for none (the line then says it isn't shown). */
const shown = (name: string | null | undefined): string | null => (name ?? '').trim() || null;

/**
 * How an imported record came in (frames r1, r2): through a connected AI (copy 7.2), or by hand.
 * Nothing for a record that wasn't imported.
 */
export function RecordLineageLine({ record }: { record: OrgBoardRecordReadOut }) {
  if (record.provenance !== 'imported') return null;
  const importedOn = displayLongDate(record.created_at) ?? '—';
  if (record.import_origin === 'connected_ai') {
    return (
      <span className={LINE} data-testid="record-detail-proposed-from">
        {w.recordLineage(
          shown(record.proposed_by_display_name),
          displayLongDate(record.proposed_at) ?? '—',
          shown(record.entered_by_display_name),
          importedOn,
        )}
      </span>
    );
  }
  return (
    <span className={LINE} data-testid="record-detail-entered-by-hand">
      {iw.enteredByHand(shown(record.entered_by_display_name), importedOn)}
    </span>
  );
}

/** A record's Board-held file: its name, kind and size, and "Open the file" ("Download the file"
 *  for a Word file, CBG-FIX160 H); and (CBG-FIX160 G) which of its pages are this record's when it
 *  is a signed set holding others, kept whole. */
export function RecordFile({ orgRef, record }: { orgRef: string; record: OrgBoardRecordOut }) {
  const operations = useBoardOperations();
  const open = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerGetBoardRecordFileUrl({
        path: { org_ref: orgRef, record_id: record.id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (link) => {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    },
    onError: (caught) =>
      toast.error(servedSentence(caught, 'We couldn’t open that file. Please try again.')),
  });
  const name = record.imported_artifact_file_name ?? '';
  const size =
    typeof record.imported_artifact_byte_size === 'number'
      ? fileSize(record.imported_artifact_byte_size)
      : null;
  const pages = record.imported_artifact_pages ?? null;
  return (
    <div className="flex flex-col gap-4 border-t border-border-subtle pt-12">
      <div
        className="flex min-w-0 flex-wrap items-center justify-between gap-x-12 gap-y-4"
        data-testid="record-detail-file"
      >
        <span className="flex min-w-0 max-w-full items-center gap-8">
          <Icon icon={FileText} size={16} aria-hidden className="shrink-0 text-icon-muted" />
          <span className="truncate font-sans text-s14 font-w500 tracking-givmo text-text-secondary">
            {name}
          </span>
          <span className="shrink-0 font-sans text-s12 tracking-givmo text-text-tertiary">
            {size === null
              ? fileKindWord(record.imported_artifact_content_type)
              : `${fileKindWord(record.imported_artifact_content_type)} · ${size}`}
          </span>
        </span>
        <button
          type="button"
          disabled={open.isPending}
          onClick={() => open.mutate()}
          className="shrink-0 font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline disabled:text-text-tertiary"
          data-testid="record-detail-open-file"
        >
          {isWordFile(record.imported_artifact_content_type) ? w.downloadTheFile : w.openTheFile}
        </button>
      </div>
      {pages !== null ? (
        <span className={LINE} data-testid="record-detail-file-pages">
          {w.recordFilePages(pages.first, pages.last)}
        </span>
      ) : null}
    </div>
  );
}
